/**
 * Keeps `?EmployeeId=&Role=` on the URL as the learner moves around (BOOT-02).
 *
 * WHY THIS EXISTS — a completion-losing bug, reproduced against the live sheet:
 *
 * Identity is captured ONCE at module load from `window.location.search`
 * (`./identity.ts`, following the `src/i18n.ts` precedent). But no `navigate()`
 * in this app preserves query parameters — `LessonStart.tsx` and
 * `TourOverlay.tsx` both build a fresh `URLSearchParams` — so the boot params
 * are gone from the URL after the learner's first tap.
 *
 * In-memory identity survives that, because it is client-side navigation. What
 * it does NOT survive is a full page load: a refresh, the host app reopening
 * the in-app browser, or the OS restoring a backgrounded tab. Those re-run the
 * boot capture against a URL that no longer carries `EmployeeId`, identity
 * becomes `null`, and from that moment every completion is recorded locally and
 * silently never sent — `reportCompletion` returns 'local-only' by design,
 * because BOOT-03 forbids writing a row keyed on nobody.
 *
 * Observed exactly this way: a learner's GUIDED stage reached the Sheet and
 * their UNGUIDED stage did not, because a reload happened in between.
 *
 * WHY NOT sessionStorage: `docs/TECH-SPEC.md:175` is explicit — "Identity is
 * never inferred from storage. It comes from the URL parameter on every load,
 * full stop. No 'remember last user'" — because on a shared store device that
 * would attribute one employee's completions to another (PITFALLS Pitfall 16).
 * Re-attaching to the URL keeps that rule intact: identity is still read from
 * the URL on every load, we simply stop throwing it away between loads.
 */
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { EMPLOYEE_PARAM, getSessionIdentity, ROLE_PARAM } from '@/session/identity'

export function useIdentityInUrl(): void {
  const [params, setParams] = useSearchParams()

  useEffect(() => {
    const identity = getSessionIdentity()
    // Nothing to re-attach: the host never identified this session, and
    // inventing a learner here would be exactly the data-integrity failure
    // BOOT-03 guards against.
    if (identity === null) return

    const wanted: [string, string][] = [[EMPLOYEE_PARAM, identity.employeeId]]
    // A role-less identity is legal (`readIdentity` admits it — progress is
    // keyed by employee alone), but writing `Role=` empty would be noise, and
    // would read back as a role the host never sent.
    if (identity.roleId !== '') wanted.push([ROLE_PARAM, identity.roleId])

    // Always write the CANONICAL spelling, never the off-case key the host may
    // have used, even though `readIdentity` now matches names in any case
    // (claim quick-kayinleong-026).
    //
    // Re-attaching onto the key already present looks tidier but is wrong: on
    // a URL carrying a lesson's demo `?role=team-lead`, `Role` would resolve to
    // that `role` key and this effect would overwrite the demo slug with the
    // learner's role code, ending the demo. Writing `Role=` alongside leaves
    // the demo param intact, and `readIdentity` prefers the exact-cased name,
    // so identity still reads back correctly on the next full load.
    //
    // The cost is a URL that briefly carries both spellings when the host used
    // an off-case name. That is harmless — same value, exact match wins — and
    // it heals on the next navigation.
    const missing = wanted.filter(([name, value]) => params.get(name) !== value)
    // Bail before touching the URL, or this effect re-triggers itself forever.
    if (missing.length === 0) return

    const next = new URLSearchParams(params)
    for (const [name, value] of missing) next.set(name, value)
    // `replace` so re-attaching never adds a history entry — otherwise Back
    // would step through a trail of identical URLs instead of leaving the screen.
    setParams(next, { replace: true })
  }, [params, setParams])
}
